/**
 * Shared behaviour of the inventory reports: warehouse / category / sub-category / search filters, loading,
 * client-side sorting and paging, PDF / Excel download, print and Chart.js charts.
 *
 * The component provides:
 *   data:     filters { warehouse, category, sub_category, search, ...its own }, sortKey, sortDir
 *   computed: visibleRows
 *   methods:  extraParams(), resetExtraFilters() (resets its own view state, returns its own filter defaults),
 *             applyResult(data | null)
 *   options:  reportUrl, fileName, columns ([{ key, numeric }])
 */
import Chart from 'chart.js/auto';
import debounce from 'lodash.debounce';
import { useToast } from 'vue-toastification';
import { apiErrorMessage } from '@/helpers/apiError';
import ReportActions from './ReportActions.vue';
import ReportChoices from './ReportChoices.vue';
import ReportPagination from './ReportPagination.vue';

export const PAGE_SIZES = [10, 25, 50, 100];

const moneyFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

export function localToday() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));
}

const PRINT_STYLES = `
  body{font-family:Arial,sans-serif;color:#092C4C;margin:24px;font-size:12px}
  h1{font-size:20px;margin:0 0 4px} .meta{color:#646B72;margin-bottom:14px}
  .cards{display:flex;gap:10px;margin-bottom:16px}
  .card{flex:1;border:1px solid #E6EAED;border-radius:6px;padding:8px 10px}
  .card small{color:#646B72;display:block} .card b{font-size:15px}
  table{width:100%;border-collapse:collapse} th{background:#092C4C;color:#fff;text-align:left}
  th,td{padding:6px 8px;border-bottom:1px solid #E6EAED;vertical-align:top}
  .num{text-align:right} .low_stock{color:#E67E22;font-weight:bold} .out_of_stock{color:#DC3545;font-weight:bold}
  .in{color:#1E9E5A} .out{color:#D63939}
  tfoot td{font-weight:bold;background:#FFF4EA} small{color:#646B72}
  @page{size:A4 landscape;margin:12mm}`;

export default {
  components: { ReportActions, ReportChoices, ReportPagination },

  setup() {
    return { toast: useToast() };
  },

  data() {
    return {
      PAGE_SIZES,
      options: { stores: [], warehouses: [], categories: [], sub_categories: [] },
      rows: [],
      loading: false,
      error: '',
      exporting: '',
      pageSize: 10,
      page: 1,
      expanded: {},
      requestId: 0,
      // The store comes from the header selection (X-Store-ID); the server reports which one it used.
      storeId: null,
      // Set while filters are changed in code, so the watchers don't each trigger a fetch.
      applyingFilters: false,
    };
  },

  computed: {
    columns() {
      return this.$options.columns;
    },

    warehouseOptions() {
      return (this.options.warehouses || []).map((w) => ({ id: w.id, text: w.name }));
    },

    categoryOptions() {
      return (this.options.categories || []).map((c) => ({ id: c.id, text: c.name }));
    },

    subCategoryOptions() {
      return (this.options.sub_categories || [])
        .filter((s) => !this.filters.category || s.category_id === this.filters.category)
        .map((s) => ({ id: s.id, text: s.name }));
    },

    itemFiltersActive() {
      const { warehouse, category, sub_category, search } = this.filters;
      return !!(warehouse || category || sub_category || search.trim());
    },

    pageCount() {
      return Math.max(1, Math.ceil(this.visibleRows.length / this.pageSize));
    },

    pageStart() {
      return (this.page - 1) * this.pageSize;
    },

    pageEnd() {
      return Math.min(this.pageStart + this.pageSize, this.visibleRows.length);
    },

    pagedRows() {
      return this.visibleRows.slice(this.pageStart, this.pageEnd);
    },

    pageNumbers() {
      const last = this.pageCount;
      if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1);
      const pages = new Set([1, last, this.page - 1, this.page, this.page + 1]);
      const sorted = [...pages].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
      return sorted.flatMap((p, i) => (i && p - sorted[i - 1] > 1 ? ['…', p] : [p]));
    },
  },

  watch: {
    'filters.warehouse'() { this.onFilterChange(); },
    'filters.category'(category) {
      // Accounting reports have no item filters; Reset still writes category, so this list may be absent.
      const sub = (this.options.sub_categories || []).find((s) => s.id === this.filters.sub_category);
      if (category && sub && sub.category_id !== category) {
        this.filters.sub_category = null;
        return;
      }
      this.onFilterChange();
    },
    'filters.sub_category'() { this.onFilterChange(); },
    'filters.search'() {
      if (!this.applyingFilters) this.debouncedFetch();
    },
    pageSize() { this.page = 1; },
    pageCount(count) {
      if (this.page > count) this.page = count;
    },
  },

  created() {
    this.debouncedFetch = debounce(this.fetchReport, 350);
    // Kept off the reactive data: Chart.js breaks when Vue wraps its instances in a proxy.
    this.charts = {};
  },

  mounted() {
    this.fetchReport();
  },

  beforeUnmount() {
    this.debouncedFetch.cancel();
    Object.values(this.charts).forEach((chart) => chart.destroy());
  },

  methods: {
    onFilterChange() {
      if (this.applyingFilters) return;
      this.debouncedFetch.cancel();
      this.fetchReport();
    },

    queryParams() {
      const { warehouse, category, sub_category, search } = this.filters;
      const params = { ...this.extraParams() };
      if (warehouse) params.warehouse = warehouse;
      if (category) params.category = category;
      if (sub_category) params.sub_category = sub_category;
      if (search.trim()) params.search = search.trim();
      return params;
    },

    async fetchReport() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = '';
      try {
        const { data } = await this.$axios.get(this.$options.reportUrl, { params: this.queryParams() });
        if (requestId !== this.requestId) return;
        this.options = data.options;
        this.storeId = data.filters?.store ?? null;
        this.rows = data.rows || [];
        this.expanded = {};
        this.page = 1;
        this.applyResult(data);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.error = apiErrorMessage(error);
        this.rows = [];
        this.applyResult(null);
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },

    resetFilters() {
      this.applyingFilters = true;
      Object.assign(this.filters, {
        warehouse: null, category: null, sub_category: null, search: '', ...this.resetExtraFilters(),
      });
      this.$nextTick(() => {
        this.applyingFilters = false;
        this.debouncedFetch.cancel();
        this.fetchReport();
      });
    },

    sortRows(rows) {
      const column = this.columns.find((c) => c.key === this.sortKey);
      const dir = this.sortDir === 'desc' ? -1 : 1;
      return [...rows].sort((a, b) => {
        const x = a[this.sortKey];
        const y = b[this.sortKey];
        const cmp = column?.numeric
          ? (Number(x) || 0) - (Number(y) || 0)
          : String(x ?? '').localeCompare(String(y ?? ''), undefined, { numeric: true });
        return cmp * dir;
      });
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = this.columns.find((c) => c.key === key)?.numeric ? 'desc' : 'asc';
      }
      this.page = 1;
    },

    sortIcon(key) {
      if (this.sortKey !== key) return 'ti-arrows-sort text-muted';
      return this.sortDir === 'asc' ? 'ti-sort-ascending' : 'ti-sort-descending';
    },

    toggleRow(key) {
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] };
    },

    goToPage(number) {
      if (number === '…') return;
      this.page = Math.min(Math.max(1, number), this.pageCount);
    },

    drawChart(name, canvas, config) {
      if (!canvas) return;
      const chart = this.charts[name];
      if (chart && chart.canvas === canvas) {
        chart.data = config.data;
        chart.options = config.options;
        chart.update();
        return;
      }
      if (chart) chart.destroy();
      this.charts[name] = new Chart(canvas, config);
    },

    formatMoney(value) {
      return moneyFormat.format(Number(value) || 0);
    },

    formatQty(value) {
      return qtyFormat.format(Number(value) || 0);
    },

    formatInt(value) {
      return (Number(value) || 0).toLocaleString('en-US');
    },

    plural(count, word) {
      const n = Number(count) || 0;
      if (n === 1) return `1 ${word}`;
      if (/[^aeiou]y$/.test(word)) return `${this.formatInt(n)} ${word.slice(0, -1)}ies`;
      return `${this.formatInt(n)} ${word}${/(s|x|ch|sh)$/.test(word) ? 'es' : 's'}`;
    },

    signed(value) {
      const n = Number(value) || 0;
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}${this.formatQty(Math.abs(n))}`;
    },

    signedMoney(value) {
      const n = Number(value) || 0;
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}${this.formatMoney(Math.abs(n))}`;
    },

    netClass(value) {
      const n = Number(value) || 0;
      return n > 0 ? 'text-success' : n < 0 ? 'text-danger' : '';
    },

    /** Bar widths for a list: each entry gets `percent` of the largest `field` (at least 4% when non-zero). */
    withPercent(list, field) {
      const max = Math.max(0, ...list.map((entry) => Number(entry[field]) || 0));
      return list.map((entry) => {
        const value = Number(entry[field]) || 0;
        return { ...entry, percent: max && value ? Math.max(4, Math.round((value / max) * 100)) : 0 };
      });
    },

    formatDate(value) {
      if (!value) return '—';
      const [year, month, day] = String(value).split('-');
      return year && month && day ? `${day}-${month}-${year}` : value;
    },

    optionName(list, id, fallback) {
      return list.find((o) => o.id === id)?.name || fallback;
    },

    itemFiltersText() {
      const search = this.filters.search.trim();
      return [
        `Store: ${this.optionName(this.options.stores, this.storeId, '—')}`,
        `Warehouse: ${this.optionName(this.options.warehouses, this.filters.warehouse, 'All')}`,
        `Category: ${this.optionName(this.options.categories, this.filters.category, 'All')}`,
        `Subcategory: ${this.optionName(this.options.sub_categories, this.filters.sub_category, 'All')}`,
        ...(search ? [`Search: ${search}`] : []),
      ];
    },

    async exportFile(type) {
      this.exporting = type;
      try {
        const response = await this.$axios.get(this.$options.reportUrl, {
          params: { ...this.queryParams(), export: type },
          responseType: 'blob',
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.data);
        link.download = `${this.$options.fileName}.${type}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        this.toast.error(apiErrorMessage(error));
      } finally {
        this.exporting = '';
      }
    },

    /** cards: [[label, value]]; values and table HTML must already be escaped. */
    openPrint(title, metaLines, cards, tableHtml) {
      const html = `<!doctype html><html><head><title>${escapeHtml(title)}</title><style>${PRINT_STYLES}</style></head><body>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">${metaLines.map(escapeHtml).join('<br>')}</div>
        <div class="cards">${cards.map(([label, value]) => `<div class="card"><small>${escapeHtml(label)}</small><b>${value}</b></div>`).join('')}</div>
        ${tableHtml}
      </body></html>`;
      const win = window.open('', '_blank', 'width=1100,height=800');
      if (!win) {
        this.toast.warning('Allow pop-ups for this site to print the report.');
        return;
      }
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => { if (!win.closed) win.print(); }, 300);
    },
  },
};
